/**
 * Combat vocabulary — the shared schema for weapons, combos and modules.
 *
 * Everything here is data-shaped on purpose. `weaponData` and `moduleData`
 * author plain objects against these interfaces; `resolve` and `wielder` are the
 * only places that interpret them. That split is what lets the loadout UI, the
 * codex and the synergy surfacer read a build without importing any simulation
 * code, and it is why nothing in this file has behaviour.
 */

import type { Vec2 } from '../core/math';
import type { MatId } from '../sim/materials';
import type { DamageTypeId } from '../entity/ecs';

// --------------------------------------------------------------- taxonomy

export type Rarity = 'common' | 'uncommon' | 'rare' | 'exotic' | 'relic';

export const RARITY_ORDER: readonly Rarity[] = ['common', 'uncommon', 'rare', 'exotic', 'relic'];

export type Archetype = 'melee' | 'projectile';

/**
 * The 18 melee + 12 projectile classes of docs/04 §3.
 *
 * `anchor` is the eighteenth and the only one added since the doc was written.
 * It exists because #100 needed a weapon whose **light** attack leaves the
 * hand: `down` both selects the heavy entry and aims the attack, so a throw
 * authored on a heavy can only ever be thrown at the floor. Moving the throw
 * onto an entry `down` does not select dissolves the collision without any aim
 * rule changing, and `axe` — which used to carry the only such throw — goes
 * back to being a weapon that swings on all seven of its nodes.
 */
export type MeleeClass =
  | 'sword' | 'dagger' | 'claw' | 'gauntlet' | 'spear' | 'lance' | 'greatblade'
  | 'hammer' | 'axe' | 'scythe' | 'whip' | 'chain' | 'shield' | 'staff'
  | 'relicBlade' | 'drill' | 'living' | 'anchor';

export type ProjectileClass =
  | 'pistol' | 'rifle' | 'scattergun' | 'launcher' | 'bow' | 'rail'
  | 'shardCaster' | 'beam' | 'drone' | 'sprayer' | 'bio' | 'thrownRig';

export type WeaponClass = MeleeClass | ProjectileClass;

export const MELEE_CLASSES: readonly MeleeClass[] = [
  'sword', 'dagger', 'claw', 'gauntlet', 'spear', 'lance', 'greatblade',
  'hammer', 'axe', 'scythe', 'whip', 'chain', 'shield', 'staff',
  'relicBlade', 'drill', 'living', 'anchor',
];

export const PROJECTILE_CLASSES: readonly ProjectileClass[] = [
  'pistol', 'rifle', 'scattergun', 'launcher', 'bow', 'rail',
  'shardCaster', 'beam', 'drone', 'sprayer', 'bio', 'thrownRig',
];

export const ALL_WEAPON_CLASSES: readonly WeaponClass[] = [
  ...MELEE_CLASSES, ...PROJECTILE_CLASSES,
];

// ---------------------------------------------------------- statuses/primers

/**
 * Status ids as stored in `World.statusKind` (a Uint8Array, so these must stay
 * small). Primers are the leading block: a primer is exactly a status that some
 * weapon knows how to detonate, which is why they share a numbering — the
 * detonation check is then a plain integer compare in the hit path.
 */
export const StatusKind = {
  NONE: 0,

  // --- primers (detonatable)
  BURNING: 1,
  FROZEN: 2,
  SHOCKED: 3,
  CORRODED: 4,
  BLEEDING: 5,
  INFECTED: 6,
  MARKED: 7,
  PHASED: 8,
  STAGGERED: 9,
  OILED: 10,
  CURSED: 11,
  IRRADIATED: 12,

  // --- non-primer statuses (no weapon detonates these)
  SLOWED: 13,
  HASTED: 14,
  WEAKENED: 15,
  ARMOR_SHRED: 16,
  LIFELINK: 17,
  RESONATING: 18,
  ECHOED: 19,
  NANITE_BOUND: 20,
  OVERHEATED: 21,
  SPORED: 22,

  // --- statuses the AI applies from the material sim. They live here rather
  //     than in enemyData because `World.statusKind` is one Uint8Array: two
  //     numberings over one array meant a weapon's PHASED (8) was read by the
  //     AI as WET (8), and a phased enemy silently conducted electricity.
  //     One enum, one array, one tick function.
  POISONED: 23,
  WET: 24,
  ENRAGED: 25,
  ATTACHED: 26,
  BUFFED: 27,
  /**
   * Issue #175, split from #159. Submersion past `DEEP_WATER_SUBMERSION`
   * (`entity/movement.ts`), climbed as an ambient status the same way
   * BURNING/POISONED are, gated on `Survivability.drowns` — a `drowns: false`
   * archetype (Possessed armor) never applies it. `game.ts`'s `stepMovement`
   * is the one caller.
   */
  DROWNING: 28,
} as const;
export type StatusKindId = (typeof StatusKind)[keyof typeof StatusKind];

/** The detonatable subset. Values are identical to their `StatusKind` twins. */
export const PrimerKind = {
  BURNING: StatusKind.BURNING,
  FROZEN: StatusKind.FROZEN,
  SHOCKED: StatusKind.SHOCKED,
  CORRODED: StatusKind.CORRODED,
  BLEEDING: StatusKind.BLEEDING,
  INFECTED: StatusKind.INFECTED,
  MARKED: StatusKind.MARKED,
  PHASED: StatusKind.PHASED,
  STAGGERED: StatusKind.STAGGERED,
  OILED: StatusKind.OILED,
  CURSED: StatusKind.CURSED,
  IRRADIATED: StatusKind.IRRADIATED,
} as const;
export type PrimerKindId = (typeof PrimerKind)[keyof typeof PrimerKind];

export const PRIMER_MIN = StatusKind.BURNING;
export const PRIMER_MAX = StatusKind.IRRADIATED;

export function isPrimer(k: number): boolean {
  return k >= PRIMER_MIN && k <= PRIMER_MAX;
}

export const STATUS_NAMES: readonly string[] = [
  'none', 'burning', 'frozen', 'shocked', 'corroded', 'bleeding', 'infected',
  'marked', 'phased', 'staggered', 'oiled', 'cursed', 'irradiated',
  'slowed', 'hasted', 'weakened', 'armorShred', 'lifelink', 'resonating',
  'echoed', 'naniteBound', 'overheated', 'spored',
  'poisoned', 'wet', 'enraged', 'attached', 'buffed',
];

// ------------------------------------------------------------------ stats

export type StatKey = 'might' | 'finesse' | 'focus' | 'resonance' | 'vitality' | 'tech';

export const STAT_KEYS: readonly StatKey[] = [
  'might', 'finesse', 'focus', 'resonance', 'vitality', 'tech',
];

/** Attacker-side stat block. Read-only from the combat system's perspective. */
export interface CombatStats {
  might: number;
  finesse: number;
  focus: number;
  resonance: number;
  vitality: number;
  tech: number;
  /** Flat additive crit chance, 0..1. */
  critChance: number;
  critMul: number;
  /** Global damage multiplier from meta-progression / relics. */
  power: number;
}

// -------------------------------------------------------------- weapon tags

export const WeaponTag = {
  FAST: 1 << 0,
  HEAVY: 1 << 1,
  REACH: 1 << 2,
  AOE: 1 << 3,
  PIERCING: 1 << 4,
  DIGGER: 1 << 5,
  UTILITY: 1 << 6,
  OFFHAND_FIRST: 1 << 7,
  PRIMER_TOOL: 1 << 8,
  DETONATOR: 1 << 9,
  CHARGEABLE: 1 << 10,
  CONTINUOUS: 1 << 11,
  SUMMONER: 1 << 12,
  PARRY: 1 << 13,
  SELF_RISK: 1 << 14,
  MOBILITY: 1 << 15,
  MATTER_AUTHOR: 1 << 16,
  RESONANT: 1 << 17,
  THROWN: 1 << 18,
  ORGANIC: 1 << 19,
} as const;
export type WeaponTagMask = number;

export function weaponTags(...names: Array<keyof typeof WeaponTag>): WeaponTagMask {
  let m = 0;
  for (const n of names) m |= WeaponTag[n];
  return m | 0;
}

// ---------------------------------------------------------------- hitboxes

export type HitboxKind =
  | 'arc' | 'box' | 'circle' | 'cone' | 'line' | 'ring' | 'point' | 'spawn';

/**
 * A hitbox in wielder-local space. `ox` is along facing, `oy` is down-positive,
 * matching the world's screen-space convention so nothing has to flip signs.
 */
export interface HitboxShape {
  kind: HitboxKind;
  /** Offset from the wielder centre, in units. `ox` is mirrored by facing. */
  ox: number;
  oy: number;
  /** Half-width (box), radius (circle/arc/ring/cone), or length (line). */
  radius: number;
  /** Half-height (box), inner radius (ring), or beam width (line). */
  thickness: number;
  /** Half-angle in radians for arc/cone; 0 otherwise. */
  halfAngle: number;
  /** Sweep centre angle relative to facing. Negative is up. */
  angle: number;
  /** Ignores line-of-sight through terrain (Void phase cuts, whips). */
  throughWalls: boolean;
}

// ------------------------------------------------------------ terrain (§6)

export interface TerrainInteraction {
  destroy: { maxHardness: number; radius: number; chance: number };
  /**
   * The damage type the blow brings to the wall — the **type route** of
   * docs/15 §3.2.
   *
   * **Never authored.** `resolveWeapon` stamps it from `ResolvedWeapon
   * .damageType` after modules are folded, so a retyping module retypes the dig
   * too and the wall and the enemy are always told the same thing. `undefined`
   * means an unresolved interaction — a raw `WeaponDef.terrain`, a harvest
   * sweep — and buys force alone, which is the pre-#132 behaviour exactly.
   */
  damageType?: DamageTypeId;
  /** Adds matter to the world — sprayers, bio-weapons, bleeding edges. */
  emit?: { mat: MatId; count: number; velocity: number };
  /** Degrees added to touched cells. Negative chills. */
  heat?: number;
  /** Charge injected into conductive/wet cells, 0..1. */
  charge?: number;
  /** Corrosion applied to touched cells, 0..1. */
  corrode?: number;
  /** Pushes loose cells around instead of deleting them. */
  displaces: boolean;
}

// ------------------------------------------------------------- combo (§4)

export interface ComboBranch {
  onLight?: string;
  onHeavy?: string;
  onDash?: string;
  onAir?: string;
}

/**
 * The four swing curves a `.pxm` weapon document's `node` line may author
 * (issue #168, `docs/29-pxm-format.md` §4.5) — the same vocabulary
 * `weaponVisual.ts`'s `SwingStyle` table already picks per *class*, spelled
 * here per *node* instead. Independently restated in
 * `src/sim/shapeMask.ts`'s `WEAPON_EASE_KINDS` rather than imported — that
 * file is `src/sim/`'s and this one is `src/combat/`'s, and the dependency
 * runs the other way (combat resolves `.pxm` documents, not the reverse).
 */
export type EaseKind = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut';

/**
 * An authored swing arc for one combo node. `start`/`end` are angles in the
 * same frame `HitboxShape.angle` uses — relative to facing, negative is up.
 */
export interface ComboArc {
  start: number;
  end: number;
  ease: EaseKind;
}

export interface ComboNode {
  id: string;
  windup: number;
  active: number;
  recovery: number;
  damageMul: number;
  poiseMul: number;
  /** Impulse applied on the first active frame, in units/sec. */
  movement: Vec2;
  hitbox: HitboxShape;
  next: ComboBranch;
  /** Fractions of the node's total duration during which a cancel is legal. */
  cancelWindow: [number, number];
  primerApplied?: PrimerKindId;
  /** Aerial node that rebounds off anything it connects with (docs/02 §4). */
  pogo?: boolean;
  /** Marks the entry node for its input class, for the state machine. */
  entry?: 'light' | 'heavy' | 'dash' | 'air';
  /** Gravity override while this node runs; undefined inherits the weapon's. */
  gravityScale?: number;
  /** Projectile weapons: what leaves the muzzle on the active frame. */
  fire?: FireSpec;
  /** An authored swing curve for this node, from a `.pxm` weapon document. */
  arc?: ComboArc;
}

export interface FireSpec {
  count: number;
  /** Cone half-angle in radians. */
  spread: number;
  speed: number;
  lifetime: number;
  pierce: number;
  gravityScale: number;
  /** Recoil impulse applied to the shooter, opposite the aim. */
  recoil: number;
  /** Cells laid down along the flight path — sprayers and bio-weapons. */
  trailMat?: MatId;
}

// ------------------------------------------------------- charge / parry

export interface ChargeState {
  id: string;
  /** Hold time in ms to reach this tier. */
  holdMs: number;
  damageMul: number;
  radiusMul: number;
  /** Node entered on release; defaults to the weapon's heavy node. */
  releaseNode?: string;
  primerApplied?: PrimerKindId;
}

export interface ParryDef {
  windowMs: number;
  recoveryMs: number;
  /** Node entered on a successful parry. */
  counterNode?: string;
  /** Sends projectiles back at their owner. */
  reflects: boolean;
  poiseGain: number;
  /** Primer stamped onto whatever was parried. */
  primerApplied?: PrimerKindId;
}

// ------------------------------------------------------------- slots (§5.1)

export type SlotKind = 'core' | 'kinetic' | 'payload' | 'trigger' | 'frame';

export const SLOT_KINDS: readonly SlotKind[] = ['core', 'kinetic', 'payload', 'trigger', 'frame'];

export interface SlotSpec {
  kind: SlotKind;
  count: number;
}

// --------------------------------------------------------------- weapon def

/**
 * An authored blade shape (issue #168, `docs/29-pxm-format.md` §4.5) — a
 * small material grid, resolved by `src/combat/weaponMask.ts`'s
 * `weaponSpecFor`. `undefined` for every weapon without a `.pxm` document
 * (29 of 30, at the time of writing) — `weaponVisual.ts`'s
 * `visualForClass` keeps deriving shape from `reach`/`weight`/class exactly
 * as it does today.
 */
export interface WeaponBlade {
  w: number;
  h: number;
  /** Row-major, length `w * h`. `VOID` where the grid is empty. */
  cells: readonly MatId[];
}

export interface WeaponDef {
  id: string;
  class: WeaponClass;
  archetype: Archetype;
  name: string;
  rarity: Rarity;

  /** An authored shape, from a `.pxm` weapon document. See `WeaponBlade`. */
  blade?: WeaponBlade;

  // handling — these define feel
  /** 0..1 master feel knob: hitstop, drag, recovery, camera kick, knockback. */
  weight: number;
  reach: number;
  /**
   * Reference point for the `cadenceMul` chain. **Not swings per second.**
   *
   * It was called `cadence` and it was read as a rate by everyone who wrote it
   * down, including docs/04 §11.1. It is not one, and it never was: the only
   * consumer of the authored number is `cadenceScale` (weaponData.ts), which
   * divides it by the *resolved* cadence — and the resolved cadence is this
   * same number multiplied by `rolled.cadenceMul` and every module's
   * `cadenceMul`. The authored magnitude therefore cancels out of its own
   * formula, exactly:
   *
   *     cadenceScale = cadenceMulBase / (cadenceMulBase × Πmul) = 1 / Πmul
   *
   * Phase 0 measured it: tripling the authored value changes the measured
   * swing rate of all 17 melee weapons by 1.00×. A designer who writes `2.1`
   * here is not authoring 2.1 swings/second; they are moving a fulcrum that
   * nothing balances on. The rate a weapon actually swings at comes entirely
   * from the node triple below (`windup`/`active`/`recovery`) and from
   * `combo`; see #39 for which of the three candidate rates is canonical.
   *
   * The name keeps `Mul` in it on purpose: the 15 modules carrying a
   * `cadenceMul` are the only reason the field exists, and a name that says so
   * cannot be misread as a unit of time.
   */
  cadenceMulBase: number;
  windup: number;
  active: number;
  recovery: number;
  /** Fraction of recovery after which a dash may cancel. */
  cancelAfter: number;
  movementVector: Vec2;
  gravityScale: number;

  // combat
  damage: number;
  damageType: DamageTypeId;
  poise: number;
  knockback: Vec2;
  combo: ComboNode[];
  chargeStates?: ChargeState[];
  parry?: ParryDef;

  // resources (§11) — the price of range
  /**
   * Rounds per magazine. Authored on the 12 projectile classes and on nothing
   * else: a melee weapon's cost is that it has to be in the room.
   *
   * A `CONTINUOUS` class spends these as tenths of a second of sustained fire
   * rather than as trigger pulls, which is why the beam's magazine reads 60 —
   * one fuel cell, six seconds.
   */
  magazine?: number;
  /** Time to refill the magazine, ms. This is the balance lever, not `damage`. */
  reloadMs?: number;
  /** Heat added per trigger pull, 0..1. Energetic classes only. */
  heatPerShot?: number;
  /** Heat added per second of sustained fire, 0..1. `CONTINUOUS` classes only. */
  heatPerSecond?: number;

  // interaction
  primes: PrimerKindId[];
  detonates: PrimerKindId[];
  terrain: TerrainInteraction;
  emits?: { mat: MatId; count: number; spread: number };

  // modularity
  slots: SlotSpec[];
  scaling: Partial<Record<StatKey, number>>;
  tags: WeaponTagMask;

  /** Off-hand-first designs (shield, staff, sprayer, drone) — docs/04 §8. */
  offhandFirst?: boolean;
  /** Codex blurb. */
  desc: string;
}

// --------------------------------------------------------------- modules

export type ModuleFamily =
  | 'frost' | 'pyre' | 'void' | 'blood' | 'fungal' | 'galvanic'
  | 'corrosive' | 'nanite' | 'radiant' | 'temporal' | 'kinetic' | 'resonant';

export const MODULE_FAMILIES: readonly ModuleFamily[] = [
  'frost', 'pyre', 'void', 'blood', 'fungal', 'galvanic',
  'corrosive', 'nanite', 'radiant', 'temporal', 'kinetic', 'resonant',
];

export type TriggerEvent =
  | 'onHit' | 'onCrit' | 'onKill' | 'onParry' | 'onPrimer' | 'onDetonate'
  | 'onDash' | 'onFinisher' | 'onLowHealth' | 'onSwap';

/**
 * Trigger events as a bitmask, so the hit path can ask "which of this weapon's
 * triggers are live right now" with one integer compare instead of a string
 * switch per module per hit.
 *
 * The existence of this mask is the whole fix for docs/04 §9: a trigger module
 * folded unconditionally into the resolved weapon is a module that is strictly
 * better than one without a condition, which the doc forbids by name.
 */
export const TriggerFlag = {
  HIT: 1 << 0,
  CRIT: 1 << 1,
  KILL: 1 << 2,
  PARRY: 1 << 3,
  PRIMER: 1 << 4,
  DETONATE: 1 << 5,
  DASH: 1 << 6,
  FINISHER: 1 << 7,
  LOW_HEALTH: 1 << 8,
  SWAP: 1 << 9,
} as const;
export type TriggerMask = number;

export const TRIGGER_FLAG_OF: Readonly<Record<TriggerEvent, TriggerMask>> = {
  onHit: TriggerFlag.HIT,
  onCrit: TriggerFlag.CRIT,
  onKill: TriggerFlag.KILL,
  onParry: TriggerFlag.PARRY,
  onPrimer: TriggerFlag.PRIMER,
  onDetonate: TriggerFlag.DETONATE,
  onDash: TriggerFlag.DASH,
  onFinisher: TriggerFlag.FINISHER,
  onLowHealth: TriggerFlag.LOW_HEALTH,
  onSwap: TriggerFlag.SWAP,
};

/** Health fraction at or below which `onLowHealth` triggers arm. */
export const LOW_HEALTH_FRAC = 0.35;

/** Self-risk is first class: the strongest modules can kill their owner (§5.3). */
export interface SelfRisk {
  kind: 'selfDamage' | 'selfStatus' | 'healthCost' | 'overheat' | 'friendlyFire';
  /** Damage per trigger, health fraction, or heat per shot depending on kind. */
  magnitude: number;
  status?: StatusKindId;
  /** Plain-language warning for the loadout UI. */
  warning: string;
}

export interface StatusApplication {
  kind: StatusKindId;
  duration: number;
  stacks: number;
  chance: number;
}

/**
 * What a module does in one archetype. Families are thematically parallel and
 * mechanically different across melee/projectile (§5.2), so every module
 * carries two of these rather than one shared blob.
 */
export interface ModuleEffect {
  /** Plain-language line the synergy UI shows verbatim. */
  summary: string;

  // scalar handling deltas — all multiplicative unless named *Add
  damageMul?: number;
  damageTypeOverride?: DamageTypeId;
  cadenceMul?: number;
  windupMul?: number;
  recoveryMul?: number;
  knockbackMul?: number;
  poiseMul?: number;
  reachMul?: number;
  weightAdd?: number;
  critChanceAdd?: number;
  critMulAdd?: number;
  /** Multiplier applied only when the target already carries a primer. */
  primedDamageMul?: number;
  /** Multiplier on the detonation payload itself. */
  detonationMul?: number;
  lifestealFrac?: number;

  // interaction
  primes?: PrimerKindId[];
  detonates?: PrimerKindId[];
  statusOnHit?: StatusApplication;
  terrain?: Partial<TerrainInteraction>;
  /** Cells sprayed/left behind by the effect. */
  emit?: { mat: MatId; count: number; velocity: number };

  // archetype-specific knobs
  melee?: {
    arcMul?: number;
    /** Material laid down along the swing path. */
    trailMat?: MatId;
    /** Charge/heat accumulated per connected hit, 0..1. */
    chargePerHit?: number;
    /** Delayed duplicate strike, in ms. 0 = none. */
    echoDelayMs?: number;
    /** Extra hits appended to the light chain. */
    extraChainHits?: number;
    parryWindowAdd?: number;
  };
  projectile?: {
    countAdd?: number;
    spreadMul?: number;
    speedMul?: number;
    pierceAdd?: number;
    ricochet?: number;
    homing?: number;
    gravityScaleMul?: number;
    lifetimeMul?: number;
    /** Recoil scaling — a movement tool on scatterguns. */
    recoilMul?: number;
    splitOnImpact?: number;
  };

  /** Trigger-slot modules only: what fires the effect. */
  trigger?: TriggerEvent;
}

export interface ModuleDef {
  id: string;
  family: ModuleFamily;
  slot: SlotKind;
  name: string;
  rarity: Rarity;
  /** Archetypes that accept this module. */
  fits: Archetype | 'both';
  onMelee: ModuleEffect;
  onProjectile: ModuleEffect;
  selfRisk?: SelfRisk;
  tags: WeaponTagMask;
  desc: string;
}

// ------------------------------------------------------------- instances

/** Per-drop stat roll. Deterministic given the instance seed. */
export interface RolledStats {
  seed: number;
  damageMul: number;
  cadenceMul: number;
  reachMul: number;
  weightAdd: number;
  critChanceAdd: number;
  critMulAdd: number;
}

export interface WeaponInstance {
  /**
   * Identity of this drop, distinct from `defId`.
   *
   * Two rolls of the same definition are two different weapons — that is the
   * entire point of `rolled` — and everything upstream used to key on `defId`,
   * so they collapsed into one row and equip/socket operations targeted
   * whichever one happened to be found first. Assigned by `rollWeapon`, opaque
   * to every reader, and stable across save/load.
   */
  id: string;
  defId: string;
  /** Installed module ids, ordered; slot legality is checked on install. */
  modules: string[];
  rolled: RolledStats;
  level: number;
  favorite: boolean;
  /**
   * The weapon's own resource, 0..1. What it *means* is a property of the class
   * (`chargeKindOf`): drill fuel, living-weapon hunger, chain spin, or charge
   * banked by `melee.chargePerHit` and spent by the heavy finisher.
   *
   * `-1` is the sentinel for "never folded". It exists because `rollWeapon`
   * knows the definition id and not the definition, so the starting value —
   * full for a fuel tank, empty for everything else — can only be decided by
   * `resolveWeapon`, which is the first place that has both.
   */
  charge: number;
  /** 0..1. Rises with sustained fire, cools while the hand is not firing. */
  heat: number;
  /** Rounds left in the magazine. `-1` is "never folded", as with `charge`. */
  ammo: number;
}

/**
 * A weapon instance flattened with its modules folded in. Rebuilt only when the
 * instance changes, never per frame — the hit path reads this and nothing else.
 */
export interface ResolvedWeapon {
  def: WeaponDef;
  instance: WeaponInstance;
  damage: number;
  damageType: DamageTypeId;
  weight: number;
  reach: number;
  /**
   * `def.cadenceMulBase × rolled.cadenceMul × every module cadenceMul`.
   *
   * Deliberately still called `cadence`, because two consumers *do* read it as
   * a rate and renaming it would have hidden that rather than exposed it:
   * `summons.ts` derives a drone's `fireIntervalMs` as `1000 / cadence`, and
   * `weaponAltar.ts` prints it to the player with a `/s` suffix. Both are
   * reading a quantity whose base is unitless (see `WeaponDef.cadenceMulBase`),
   * which is a live inconsistency — flagged for #39, deliberately not fixed
   * here, because changing either one is a balance decision.
   *
   * Everything on the swing path uses only the *ratio* `cadenceMulBase /
   * cadence`, never this value on its own.
   */
  cadence: number;
  poise: number;
  critChance: number;
  critMul: number;
  primedDamageMul: number;
  detonationMul: number;
  lifestealFrac: number;
  knockbackMul: number;
  recoveryMul: number;
  windupMul: number;
  primes: PrimerKindId[];
  detonates: PrimerKindId[];
  statuses: StatusApplication[];
  terrain: TerrainInteraction;
  risks: SelfRisk[];
  /**
   * Parallel to `risks`: the trigger mask each risk is gated on, 0 for
   * unconditional. A conditional module must not charge its cost while its
   * benefit is switched off — `blood_trigger_sacrifice` is "only active below
   * 30% health", and billing 3% of max health per hit at full health is the
   * same §9 violation as granting its crit for free, pointed the other way.
   */
  riskMasks: TriggerMask[];
  triggers: Array<{ event: TriggerEvent; mask: TriggerMask; effect: ModuleEffect; module: ModuleDef }>;
  /** Every trigger event this weapon carries, OR-ed. Zero for most weapons. */
  triggerMask: TriggerMask;
  /** Parry window in ms after `melee.parryWindowAdd`; 0 when the class cannot parry. */
  parryWindowMs: number;
  projectile: {
    countAdd: number;
    spreadMul: number;
    speedMul: number;
    pierceAdd: number;
    ricochet: number;
    homing: number;
    gravityScaleMul: number;
    lifetimeMul: number;
    recoilMul: number;
    splitOnImpact: number;
  };
  melee: {
    arcMul: number;
    trailMat: MatId;
    chargePerHit: number;
    echoDelayMs: number;
    extraChainHits: number;
    parryWindowAdd: number;
  };
  /** Node lookup, built once so the state machine never scans the array. */
  nodes: Map<string, ComboNode>;
  /** Entry points per input class, resolved once rather than searched. */
  entries: {
    light: ComboNode;
    heavy: ComboNode;
    dash: ComboNode;
    air: ComboNode;
    dair: ComboNode;
  };
}

// -------------------------------------------------------------- loadout

export type Hand = 'main' | 'off';

/** One of the 8 weapon slots of §7. `hand` is null when merely stowed. */
export interface LoadoutSlot {
  index: number;
  instance: WeaponInstance | null;
  hand: Hand | null;
  favorite: boolean;
}
